import { zeroTax } from "../../contexts/BudgetContext/reducer-funcion";
import { createOptionsFromEnum } from "../../utils/formatters";
import {
  AppliedTaxes,
  NFMMOptions,
  ServiceData,
  TaxData,
} from "./service-item";

export enum BudgetType {
  LOGISTICA = "LOGISTICS",
  PRODUCAO = "PRODUCTION",
}

export type Budget = {
  project: string;
  _id?: string;
  generalInfo: ComparisonInfo[];
  status?: StatusBudget;
  name?: string;
  type?: BudgetType;
  budgetServices: ServiceData[];
  itemTypeList: ItemTypeListPerCalcOption;
  addedItemTypeList: AddedTypes[];
  supplierList: AddedSupplier[];
  /**
   * Agrupamentos de fornecedores ("Dia 1", "Pré-evento"…). A ORDEM DO ARRAY é a
   * ordem exibida — não existe campo `order` aqui, diferente de
   * `AddedTypes.order`: esta lista não é plana/compartilhada entre fornecedores,
   * então a posição do array basta e evita dois lugares para consultar ordem.
   *
   * Opcional: orçamento gravado antes desta funcionalidade não tem o campo.
   */
  budgetGroups?: BudgetGroup[];
  budgetValuesTotal: VersionTotals;
  paymentSolicitation: IPaymentSolicitationByType[];
  files: AttachedFiles[];
  negociationValuesTotal: VersionTotals[];
  closingValuesTotal: VersionTotals;
  isApiUpdate?: boolean;
  negotiationVersion: number;
  versionHistory: versionInfo[];
  budgetIndex: number;
  baseValues: BaseValue[];
  mainAppliedTaxes: MainAppliedTaxes[];
  budgetWideAppliedTaxes: BudgetWideAppliedTaxes[];
  noteAdvances?: NoteAdvance[];
  /**
   * Monotonic server totals cache version (BE-P4-03 / FE-P4-01).
   * Present on v2 shell/summary; used to detect stale client totals snapshots.
   */
  totalsVersion?: number;
  updatedAt?: string;
};

export interface BudgetWideAppliedTaxes {
  taxData: TaxData;
  exchange: ExchangeData;
  version: number;
  applyToAllServices: boolean;
}

/**
 * Agrupamento dentro de UMA forma de cobrança ("Dia 1", "Pré-evento").
 *
 * Camada apenas organizacional: não entra em nenhum cálculo. O vínculo mora em
 * `ServiceData.groupId` — a linha exibida no agrupamento é o par (fornecedor,
 * categoria) DERIVADO dos serviços que estão nele.
 *
 * O agrupamento é lista própria no documento porque pode existir VAZIO, antes
 * de receber qualquer serviço.
 */
export type BudgetGroup = {
  /**
   * Identidade estável: `key` do React, id do `@dnd-kit` e alvo de
   * `AddedSupplier.groupId`. Mesmo papel do `uid` da linha de fornecedor — o
   * nome não serve, porque é editável e pode repetir.
   */
  id: string;
  name: string;
  calcOption: CalcOption;
  /**
   * Etapas em que o agrupamento foi excluído. Mesmo contrato de
   * `AddedSupplier.deletedInVersions`: excluir na Negociação não pode apagar o
   * agrupamento do Orçamento base.
   */
  deletedInVersions?: number[];
};

/** Adiantamentos informados na seção "Resumo de Nota" da tela de Totais (por versão). */
export interface NoteAdvance {
  version: number;
  advanceDebitNote: number;
  advanceTaxNote: number;
}

export const initialNoteAdvance: NoteAdvance = {
  version: 0,
  advanceDebitNote: 0,
  advanceTaxNote: 0,
};

export const initialBudgetWideAppliedTaxes: BudgetWideAppliedTaxes = {
  taxData: {
    tariff: { ...zeroTax },
    serviceTax: { ...zeroTax },
    localTax: { ...zeroTax },
    tourismTax: { ...zeroTax },
    iof: { ...zeroTax },
    fee: { ...zeroTax },
    iss: { ...zeroTax },
    cbs: { ...zeroTax },
    ibs: { ...zeroTax },
    typeNfMm: "A",
    overhead: { ...zeroTax },
    markup: { ...zeroTax },
    commission: { ...zeroTax },
    incentive: { ...zeroTax },
    tribute: { ...zeroTax },
    supplier_tribute: { ...zeroTax },
    doubleCharged: { ...zeroTax },
    extras: { ...zeroTax },
  },
  exchange: { date: "", currency: "BRL", value: 1 },
  version: 0,
  applyToAllServices: false,
};
export interface MainAppliedTaxes {
  // main because it has a similar prop in services
  version: number;
  supplierId: string;
  category: BudgetItemType;
  calcOption: CalcOption;
  appliedTaxes: AppliedTaxes;
}
export const editBitributedRoles = [
  "Administrador",
  "Gerente",
  "Diretoria",
  "Supervisor",
];
export const initialMainAppliedTaxes: MainAppliedTaxes = {
  version: 0,
  supplierId: "",
  category: "" as BudgetItemType,
  calcOption: "A",
  appliedTaxes: {
    serviceTax: true,
    iss: true,
    cbs: true,
    ibs: true,
    tribute: true,
    supplier_tribute: true,
    localTax: true,
    tourismTax: true,
    fee: true,
    iof: true,
    markup: true,
    overhead: true,
    commission: true,
    incentive: true,
    doubleCharged: true,
  },
};
export interface BaseValue {
  version: number;
  supplierId: string;
  category: BudgetItemType;
  calcOption: CalcOption;
  taxData: TaxData;
  exchange: ExchangeData;
  isOverride?: boolean; // Flag para indicar se o câmbio foi editado manualmente
  taxDataOverride?: boolean; // Flag para indicar se as taxas foram editadas manualmente (não deve ser sobrescrita pelas taxas de Totais)
  descriptions: {
    composition: string;
    cancelPolicy: string;
    paymentMethod: string;
  };
}

export type versionInfo = {
  version: number;
  comparisonVersion: number;
  status: string;
  isConsulting?: boolean;
};

export type VersionTotals = {
  byCalculationType: AllTotals;
  byItemType: ByItemType;
};
export type CalcOption = "A" | "B" | "C" | "D";

export type AttachedFiles = {
  calcOption: CalcOption;
  supplierId: string;
  type: BudgetItemType;
  files: File[];
};

export type AddedSupplier = {
  supplierId: string;
  calcOption: CalcOption;
  /**
   * Identidade estável da LINHA de fornecedor, independente de posição e do
   * `supplierId` selecionado. Necessária para o drag and drop: o `supplierId`
   * nasce vazio (`ADD_SUPPLIER`) e pode repetir entre linhas, e o índice muda
   * a cada reordenação — nenhum dos dois serve como `key` do React nem como id
   * do `@dnd-kit`. Opcional porque orçamentos antigos não têm o campo; o
   * reducer faz backfill ao carregar (ver `withSupplierUids`).
   */
  uid?: string;
  /**
   * Versões (etapas) em que esta LINHA foi excluída. A `supplierList` é
   * compartilhada por Orçamento/Negociação/Fechamento e não pode ser podada numa
   * etapa versionada; normalmente quem esconde o card é `service.versions`, mas
   * fornecedor sem nenhum serviço não tem de onde derivar isso. Ver
   * `utils/budget-stage-visibility.ts`.
   */
  deletedInVersions?: number[];
  descriptions?: {
    composition: string;
    cancelPolicy: string;
    paymentMethod: string;
    commercialConditions?: boolean;
  };
};

export type ItemTypeList = {
  AIR?: boolean;
  AEB?: boolean;
  PRODUCTION_SUPPORT?: boolean;
  ATTRACTION?: boolean;
  BRINDES_INCENTIVOS_PREMIACOES?: boolean;
  CENOGRAPHY_DECORATION?: boolean;
  COMMUNICATIONS?: boolean;
  CONTENT?: boolean;
  EQUIPMENTS?: boolean;
  PARKING?: boolean;
  // SUPPORT?: boolean;
  FOTO_VIDEO?: boolean;
  ACCOMMODATIONS?: boolean;
  SUBSCRIPTION?: boolean;
  LICENCES_TAXES?: boolean;
  GRAPHIC_MATERIAL?: boolean;
  MOUNTING_COMPANY?: boolean;
  NECESSARY_ADDITIONS?: boolean;
  // SEVERAL?: boolean;
  HUMAN_RESOURCES_STAFF?: boolean;
  RSVP?: boolean;
  ROOMS?: boolean;
  TRAVEL_INSURANCE?: boolean;
  SERVICE_SUPPORT?: boolean;
  EVENT_INSURANCE?: boolean;
  TECHNOLOGY?: boolean;
  TRANSLATIONS?: boolean;
  TRANSFER?: boolean;
};
export type ItemTypeListPerCalcOption = {
  A: ItemTypeList;
  B: ItemTypeList;
  C: ItemTypeList;
  D: ItemTypeList;
  budgetTotals: any;
};
export type AddedTypes = {
  type: BudgetItemType;
  supplierId: string;
  calcOption: HeaderOptions;
  /**
   * Posição da categoria dentro do fornecedor, definida pelo usuário via drag
   * and drop. Ausente ⇒ a categoria segue a ordem fixa de
   * `CATEGORY_PRIORITY_ORDER` (comportamento histórico, preservado para todo
   * orçamento que nunca foi reordenado).
   */
  order?: number;
  /**
   * Versões (etapas) em que esta categoria foi excluída — mesmo contrato de
   * `AddedSupplier.deletedInVersions`, para a categoria vazia (sem serviço) poder
   * sumir da etapa sem sair do orçamento base.
   */
  deletedInVersions?: number[];
};

export type TotalByCalcOption = {
  [key in NFMMOption]: TotalByType;
};

export interface AllTotals extends TotalByCalcOption {
  budgetTotals: SumsTotalByType;
}
// Nova tipagem criada pois a resposta da API está diferente da tipagem proposta
export interface ByItemType extends SumsTotalByType {
  SUM: Totals;
}

export type TotalByType = {
  [key in BudgetItemType]: TotalSplited[];
} & {
  SUM: TotalSplited;
};
export type SumsTotalByType = {
  [key in BudgetItemType]: Totals;
} & {
  SUM: Totals;
};
export type NFMMCalcItem = {
  [key in NFMMOption]: TotalSplited;
};
export type StepTotals = {
  budgetValues: Totals;
  negociationValues: Totals[];
  closingValues: Totals;
};
export type TotalSplited = {
  supplierId?: string;
  calcOption?: CalcOption;
  taxes: Totals;
  totals: Totals;
  revenueTotals: Totals;
  serviceTaxTotals: Totals;
  tariffTotals: Totals;
  localTaxTotals: Totals;
  tourismTaxTotals: Totals;
  iofTotals: Totals;
  feeTotals: Totals;
  issTotals: Totals;
  cbsTotals: Totals;
  ibsTotals: Totals;
  overheadTotals: Totals;
  markupTotals: Totals;
  doubleChargedTotals: Totals;
  commissionTotals: Totals;
  incentiveTotals: Totals;
  tributeTotals: Totals;
  supplierTributeTotals: Totals;
  extraTotals: Totals;
};
export type Totals = {
  totalInBRL: number;
  totalInCurrency: number;
};

const emptyTotals: Totals = {
  totalInBRL: 0,
  totalInCurrency: 0,
};

export const emptyTotalSplited: TotalSplited = {
  taxes: { ...emptyTotals },
  totals: { ...emptyTotals },
  revenueTotals: { ...emptyTotals },
  serviceTaxTotals: { ...emptyTotals },
  tariffTotals: { ...emptyTotals },
  localTaxTotals: { ...emptyTotals },
  tourismTaxTotals: { ...emptyTotals },
  iofTotals: { ...emptyTotals },
  feeTotals: { ...emptyTotals },
  issTotals: { ...emptyTotals },
  cbsTotals: { ...emptyTotals },
  ibsTotals: { ...emptyTotals },
  overheadTotals: { ...emptyTotals },
  markupTotals: { ...emptyTotals },
  doubleChargedTotals: { ...emptyTotals },
  commissionTotals: { ...emptyTotals },
  incentiveTotals: { ...emptyTotals },
  tributeTotals: { ...emptyTotals },
  supplierTributeTotals: { ...emptyTotals },
  extraTotals: { ...emptyTotals },
};

export type NFMMOption = CalcOption | "SUM";
export type HeaderOptions = "A" | "B" | "C" | "D" | "budgetTotals";
export interface IExchangeAndDescriptionByType {
  _id: string;
  supplierId: string;
  type: BudgetItemType;
  negotiationVersion: number;
  exchangeData: ExchangeData;
  calcOption: HeaderOptions;
  descriptions: {
    composition: string;
    cancelPolicy: string;
    paymentMethod: string;
  };
}
export interface IPaymentSolicitationByType {
  _id: string;
  supplierId: string;
  type: BudgetItemType;
  calcOption: NFMMOption;
  exchangeData: ExchangeData;
  billing: string;
  comisionType: string;
  paymentMethod: PaymentMethods[];
  paymentMethodDescription: string;
  downPaymentExchangeData: ExchangeData;
}

export interface CategoryTotals {
  totals: Totals;
  revenueTotals: Totals;
  serviceTaxTotals: Totals;
  tariffTotals: Totals;
  issTotals: Totals;
  cbsTotals: Totals;
  ibsTotals: Totals;
  iofTotals: Totals;
  localTaxTotals: Totals;
  doubleChargedTotals: Totals;
  tourismTaxTotals: Totals;
  feeTotals: Totals;
  markupTotals: Totals;
  overheadTotals: Totals;
  commissionTotals: Totals;
  incentiveTotals: Totals;
  tributeTotals: Totals;
  supplierTributeTotals: Totals;
  taxes: Totals;
  extraTotals: Totals;
}
export const keysCategoryTotalsBRL: (keyof CategoryTotals)[] = [
  "totals",
  "revenueTotals",
  "serviceTaxTotals",
  "tariffTotals",
  "issTotals",
  "cbsTotals",
  "ibsTotals",
  "localTaxTotals",
  "tourismTaxTotals",
  "doubleChargedTotals",
  "feeTotals",
  "markupTotals",
  "overheadTotals",
  "commissionTotals",
  "incentiveTotals",
  "tributeTotals",
  "supplierTributeTotals",
  "taxes",
  "iofTotals",
  "extraTotals",
];

type BudgetGeneralInfo = {
  info: ComparisonInfo;
  version: number;
};
type ComparisonInfo = {
  definition: string;
  rfp: boolean;
  tagGMP: string;
  local: string;
  city: string;
  country: string;
  state: string;
  address: string;
  checkIn: string;
  checkOut: string;
  meetingPeriod: string;
  availability: string;
  deadline: string;
  site: string;
};

export const initCategoryTotals: CategoryTotals = {
  totals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  extraTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  revenueTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  serviceTaxTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  tariffTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  issTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  cbsTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  ibsTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  doubleChargedTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  localTaxTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  tourismTaxTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  feeTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  markupTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  overheadTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  commissionTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  incentiveTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  tributeTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  supplierTributeTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  taxes: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  iofTotals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
};
export const initialPaymentSolicitation: IPaymentSolicitationByType = {
  _id: "new",
  supplierId: "",
  type: "" as BudgetItemType, // Default placeholder, replace as needed
  calcOption: "" as NFMMOption, // Default placeholder, replace as needed
  exchangeData: {
    date: "",
    currency: "BRL",
    value: 1,
  },
  billing: "",
  comisionType: "",
  paymentMethod: [], // Starts as an empty array
  paymentMethodDescription: "",
  downPaymentExchangeData: {
    date: "",
    currency: "BRL",
    value: 1,
  },
};

export enum PaymentMethods {
  CREDIT_CARD = "Cartão de Crédito",
  DEBIT_CARD = "Cartão de Débito",
  CASH = "Dinheiro",
  BANK_TRANSFER = "Transferência Bancária",
  PIX = "PIX",
  BOLETO = "Boleto",
  PAYPAL = "PayPal",
  CRYPTOCURRENCY = "Criptomoeda",
  VOUCHER = "Vale/ Voucher",
}
export interface ExchangeData {
  date: string;
  currency: string;
  value: number;
}

export type StatusBudget = keyof typeof BudgetStatusEnum;

/**
 * Negociar/aprovar anexam uma nova entrada ao final de versionHistory; reprovar
 * atualiza a entrada da versão corrente in-place. A última entrada (maior version)
 * é, portanto, o status efetivo/atual do orçamento — versionHistory[0] fica
 * parado em "OPEN" para sempre e não deve ser usado para exibir o status atual.
 */
export function getBudgetCurrentStatus(
  versionHistory: { version: number; status: string }[] | undefined
): StatusBudget {
  if (!versionHistory?.length) return "OPEN" as StatusBudget;
  const latest = versionHistory.reduce((max, v) =>
    v.version > max.version ? v : max
  );
  return (latest.status || "OPEN") as StatusBudget;
}

export enum CalcTypeText {
  A = "[A] Pagamento direto pelo cliente (Cartão de Crédito, EBTA ou PO direta)",
  B = "[B] Nota de débito (faturados contra o cliente por meio de Nota de Débito MM)",
  C = "[C] Custos de terceiros (faturados contra a MM e cobrados do cliente em Nota Fiscal MM)",
  D = "[D] Custos internos (cobrados do cliente em Nota Fiscal MM – sem honorários)",
}

export enum BudgetStatusEnum {
  OPEN = "Aberto",
  APPROVED = "Aprovado",
  NEGOTIATE = "Em Negociação",
  APPROVED_NEGOTIATION = "Negociação Aprovada",
  DENIED = "Reprovado",
  DENIED_CLOSING = "Reprovado",
  AUDITED = "Auditado",
  ON_BUDGET = "Em orçamento",
  ON_APPROVAL = "Em aprovação",
  CLOSURE = "Fechamento",
  PRE_CLOSING = "Pré Fechamento",
  RETURNED_FOR_ADJUSTMENTS = "Devolvido para Ajustes",
  DELETED = "Apagado",
  CONSULTING = "Assessoria",
}

export enum BudgetStatus {
  OPEN = "OPEN",
  APPROVED = "APPROVED",
  NEGOTIATION = "NEGOTIATION",
  APPROVED_NEGOTIATION = "APPROVED_NEGOTIATION",
  DECLINED = "DECLINED",
  AUDITED = "AUDITED",
  ON_BUDGET = "ON_BUDGET",
  ON_APPROVAL = "ON_APPROVAL",
  CLOSURE = "CLOSURE",
}
export type BudgetItemType =
  | "AEB"
  | "AIR"
  | "ATTRACTION"
  | "PRODUCTION_SUPPORT"
  | "BRINDES_INCENTIVOS_PREMIACOES"
  | "CENOGRAPHY_DECORATION"
  | "COMMUNICATIONS"
  | "CONTENT"
  | "EQUIPMENTS"
  // | "SUPPORT"
  | "FOTO_VIDEO"
  | "ACCOMMODATIONS"
  | "SUBSCRIPTION"
  | "LICENCES_TAXES"
  | "GRAPHIC_MATERIAL"
  | "MOUNTING_COMPANY"
  | "NECESSARY_ADDITIONS"
  // | "SEVERAL"
  | "HUMAN_RESOURCES_STAFF"
  | "RSVP"
  | "ROOMS"
  | "TRAVEL_INSURANCE"
  | "SERVICE_SUPPORT"
  | "EVENT_INSURANCE"
  | "TECHNOLOGY"
  | "TRANSLATIONS"
  | "TRANSFER"
  | "PARKING";

export enum BudgetItemTypeEnum {
  AIR = "Aéreo",
  AEB = "Alimentos e Bebidas",
  PRODUCTION_SUPPORT = "Atendimento Produção",
  ATTRACTION = "Atração",
  BRINDES_INCENTIVOS_PREMIACOES = "Brindes, Incentivos e Premiações",
  CENOGRAPHY_DECORATION = "Cenografia e Decoração",
  COMMUNICATIONS = "Comunicação",
  CONTENT = "Conteúdo",
  EQUIPMENTS = "Equipamentos",
  PARKING = "Estacionamento",
  // SUPPORT = "Equipe e suporte",
  FOTO_VIDEO = "Foto e Vídeo",
  ACCOMMODATIONS = "Hospedagem",
  SUBSCRIPTION = "Inscrição",
  LICENCES_TAXES = "Licenças e Taxas Obrigatórias",
  GRAPHIC_MATERIAL = "Material Gráfico",
  MOUNTING_COMPANY = "Montadora",
  NECESSARY_ADDITIONS = "Necessidades Adicionais",
  // SEVERAL = "Outros",
  HUMAN_RESOURCES_STAFF = "Recursos Humanos",
  RSVP = "RSVP",
  ROOMS = "Salas e Espaços",
  TRAVEL_INSURANCE = "Seguro Freelancer",
  EVENT_INSURANCE = "Seguro do Evento",

  SERVICE_SUPPORT = "Serviço de Apoio",
  TECHNOLOGY = "Tecnologia",
  TRANSLATIONS = "Tradução",
  TRANSFER = "Transfer",
}

export enum EventLocation {
  RECEPTION = "RECEPÇÃO",
  FOYER = "FOYER",
  FOYER_2 = "FOYER 2",
  AUDITORIUM_1 = "PLENÁRIA 1",
  AUDITORIUM_2 = "PLENÁRIA 2",
  COCKTAIL = "COQUETEL",
  LUNCH = "ALMOÇO",
  OTHERS = "OUTROS",
  AUDITORIUM = "PLENÁRIA",
  TECHNOLOGICAL_ACTIVATION = "ATIVAÇÃO TECNOLÓGICA",
  GASTRONOMIC_ACTIVATION = "ATIVAÇÃO GASTRONOMICA",
  STAFF_ROOM = "SALA DE STAFF",
}

export enum BudgetItemTotalRelation {
  hotels = "Hospedagem",
  rooms = "Salas",
  foodAndDrinks = "A & B e catering",
  equipment = "Equipamentos",
  simultaneousTranslation = "Tradução simultânea",
  suportTeams = "Equipe e suporte",
  subscription = "Inscrição",
  rsvp = "RSVP",
  air = "Aéreo",
  transfer = "Transfer",
  communications = "Comunicação",
  several = "Outros",
}
export const itemOptions = createOptionsFromEnum(BudgetItemTypeEnum);
export enum BudgetTotalsCosts {
  PREVISION = "Custo total previsão",
  SERVICE = "Taxa de Serviço",
  ISS = "ISS",
  TARIFF = "Tarifa",
  LOCAL_TAX = "Imposto Local",
  TOURISM_TAX = "Taxa de Turismo",
  FEE = "Taxa MM",
  NFMM = "Tipo de NF MM",
  EXCHANGE = "Cambio",
  NFMM_TAX = "Impostos NF MM",
  EXTRAS_PREVISION = "Previsão de extras",
  TOTAL_COST_PREVISION = "Total previsão de custos",
}
export enum BudgetCostsKeysToTotals {
  PREVISION = "costForecastSubtotal",
  SERVICE = "totalServiceTax",
  ISS = "ISS",
  TARIFF = "totalTariff",
  IOF = "totalTaxIof",
  MM_TAX = "Taxa MM eventos(fees)",
  EXCHANGE = "totalExchange",
  NFMM_TAX = "totalNfMm",
  EXTRAS_PREVISION = "extras",
  TOTAL_COST_PREVISION = "total",
}

export enum BudgetSummaryActionOptions {
  DUPLICATE = "Duplicar",
  DELETE = "Excluir",
  APROVE = "Enviar para fechamento",
  DENY = "Reprovar",
  NEGOTIATE = "Enviar para negociação",
  NEW_VERSION = "Criar nova versão",
  RENAME = "Renomear",
  CONSULTING = "Criar versão assessoria",
  // IMPORT = 'Importar de logística',
  // ORDER_ASC = 'Ordenar valores do maior para o menor',
  // ORDER_DESC = 'Ordenar valores do menor para o maior'
}

export type { BudgetSummary } from "./budget-summary";
